import type { ChartPayload } from '@scryerlabs/ui/charts'
import type { StrategyDataAlertOutcome } from './strategy-data-alert.types'

// Trading strategy status enum matching backend TradingStrategyStatus
export enum TradingStrategyStatus {
  Unknown = 0,
  Draft = 1,
  Published = 2,
  Archived = 3,
}

export enum TradingStrategyChartType {
  Unknown = 0,
  FrontOffice = 1,
  Brochure = 2,
}

// What a chart shows, and so which builder draws it from the archive.
export enum TradingStrategyChartKind {
  Unknown = 0,
  EquityCurve = 1,
  AnnualReturns = 2,
  Candles = 3,
}

// How a stat's numeric value is written out.
export enum TradingStrategyStatFormat {
  Unknown = 0,
  Percent = 1,
  SignedPercent = 2,
  PercentPerYear = 3,
  RatioOfYears = 4,
  Count = 5,
}

// Which sides a strategy takes, read off its config. Mirrors backend StrategyDirection.
export enum StrategyDirection {
  Unknown = 0,
  LongOnly = 1,
  ShortOnly = 2,
  LongAndShort = 3,
}

// Which archive layout an export was produced against.
export enum StrategyArchiveVersion {
  Unknown = 0,
  V1 = 1,
}

// Stat display tone matching backend TradingStrategyStatTone
export enum TradingStrategyStatTone {
  Unknown = 0,
  Neutral = 1,
  Positive = 2,
  Negative = 3,
}

// Response type for GET /api/trading-strategies (list)
export interface TradingStrategyListItem {
  id: string
  publicStrategyId: string
  publicStrategyName: string
  publicName: string
  description: string
  status: TradingStrategyStatus
  monthlyPriceUsd: number
  yearlyPriceUsd: number
  showOnBrochureSite: boolean
  assetClass: number
  timeframe: number
  riskProfile: number
  // Which sides the strategy takes. Unknown where nobody has established it.
  direction: StrategyDirection
  // Null on a strategy recorded before instruments were linked.
  chartedSymbolTicker?: string | null
  // Where the orders go on each side. Null where the strategy trades its charted symbol directly.
  bullSymbolTicker?: string | null
  bearSymbolTicker?: string | null
  chartFrom: string
  chartTo: string
  createdDate: string
  lastModifiedDate: string
  /** The last day the stored data holds. Null when nothing is stored. */
  storedDataTo?: string | null
  /** When the latest data update applied to the stored data was received. Null when none has been. */
  lastUpdateReceivedAt?: string | null
  /** Days the stored data may be missing, oldest first. */
  storedDataGaps: TradingStrategyStoredDataGap[]
  /** When staff turned on real-time updates. Null while they are off. */
  realTimeUpdatesSince?: string | null
}

export interface TurnOnRealTimeUpdatesResponse {
  /** The last day the figures and charts sent to customers now cover. */
  figuresTo: string
}

/** The last day held before a gap in a strategy's stored data, and the first day held after it. */
export interface TradingStrategyStoredDataGap {
  lastDayBefore: string
  firstDayAfter: string
}

/** A week without an update means even a weekly post has been missed. */
export const STORED_DATA_QUIET_AFTER_DAYS = 7

/** What customers see of a strategy's performance, what its stored data holds, and its latest data updates. */
export interface TradingStrategyStoredData {
  /** Null with no uploaded dump. */
  dumpUploadedAt?: string | null
  dumpLastDay?: string | null
  /** Null when nothing is stored. */
  storedData?: StrategyStoredData | null
  recentUpdates: StrategyDataUpdate[]
}

/** Every day held, the dump's and the ones updates added after its last day. */
export interface StrategyStoredData {
  from: string
  to: string
  days: number
  updateDays: number
  closedTrades: number
  openTrades: number
  lastUpdateReceivedAt?: string | null
  gaps: TradingStrategyStoredDataGap[]
}

/** One saved post for the strategy and what became of it. */
export interface StrategyDataUpdate {
  id: string
  receivedAt: string
  firstDay?: string | null
  lastDay?: string | null
  outcome?: StrategyDataAlertOutcome | null
}

export interface TradingStrategyStat {
  id: string
  statKey?: string | null
  label: string
  value: string
  displayOrder: number
  showOnFrontOffice: boolean
  showOnBrochureSite: boolean
  isHighlight: boolean
  tone: TradingStrategyStatTone
  // The figure behind `value`, or null for a stat that is text rather than a measurement.
  numericValue?: number | null
  outOf?: number | null
  format: TradingStrategyStatFormat
  // What the archive stated, kept alongside any correction.
  derivedValue?: number | null
  // What an admin stated by hand, which an import cannot overwrite.
  overrideValue?: number | null
  isOverridden: boolean
}

export interface TradingStrategyChart {
  id: string
  name: string
  blobName: string
  displayOrder: number
  chartType: TradingStrategyChartType
  kind: TradingStrategyChartKind
  blobExists?: boolean
}

// One trade the backtest took, as the customer will see it.
export interface TradingStrategyTrade {
  tradeNumber: number
  direction: string
  entryDate: string
  entryPrice?: number | null
  exitDate: string
  exitPrice?: number | null
  returnPct: number
  returnValue: string
}

export interface TradingStrategyAnnualisedReturn {
  series: string
  period: string
  label: string
  value: string
  numericValue: number
  displayOrder: number
}

// The export a strategy's figures were derived from. Describes the file rather than carrying it;
// the bytes come from the download endpoint.
export interface TradingStrategyArchive {
  originalFileName: string
  version: StrategyArchiveVersion
  publicStrategyId: string
  publicStrategyName: string
  config: string
  // The source configs the script was built from. Absent where it has no such side.
  configLong?: string | null
  configShort?: string | null
  symbol: string
  chartFrom: string
  chartTo: string
  // The Start Limit the export stated. Null where it named none.
  backtestFrom: string | null
  exportedOn: string
  uploadedAt: string
  // Which sides the script takes, as its config states. Unknown where the config does not say.
  direction: StrategyDirection
}

// Response type for GET /api/trading-strategies/{id}
export interface TradingStrategyDetail {
  id: string
  publicStrategyId: string
  publicStrategyName: string
  publicName: string
  description: string
  status: TradingStrategyStatus
  monthlyPriceUsd: number
  yearlyPriceUsd: number
  showOnBrochureSite: boolean
  assetClass: number
  timeframe: number
  riskProfile: number
  // Which sides the strategy takes. Unknown where no archive has stated it.
  direction: StrategyDirection
  chartFrom: string
  chartTo: string
  // The earliest date TradingView will let a customer backtest from. Null where the export
  // did not state one.
  backtestFrom: string | null
  // Where the script is published on TradingView. Null where staff recorded none.
  publicationUrl: string | null
  createdDate: string
  lastModifiedDate: string
  // Null on strategies recorded before the links existed.
  chartedSymbolId?: string | null
  chartedSymbolTicker?: string | null
  bullSymbolId?: string | null
  bullSymbolTicker?: string | null
  bearSymbolId?: string | null
  bearSymbolTicker?: string | null
  stats: TradingStrategyStat[]
  charts: TradingStrategyChart[]
  annualisedReturns: TradingStrategyAnnualisedReturn[]
  trades: TradingStrategyTrade[]
  archive?: TradingStrategyArchive | null
  /** When staff turned on real-time updates. Null while they are off. */
  realTimeUpdatesSince?: string | null
  /** Who turned real-time updates on. Null while they are off. */
  realTimeUpdatesBy?: string | null
}

export interface TradingStrategyStatRequest {
  id?: string
  statKey?: string | null
  label: string
  value: string
  displayOrder: number
  showOnFrontOffice: boolean
  showOnBrochureSite: boolean
  isHighlight: boolean
  tone: TradingStrategyStatTone
  // Required for one of the nine: the API refuses a core stat that offers only text.
  numericValue?: number | null
  outOf?: number | null
}

export interface TradingStrategyChartRequest {
  id?: string
  name: string
  displayOrder: number
  chartType: TradingStrategyChartType
  // A chart is drawn from the archive, so a request names what it wants rather than uploading it.
  kind: TradingStrategyChartKind
}

// What POST /api/trading-strategies/archive/parse gives back. Saves nothing, so it can be
// called as often as needed while trying files.
export interface ParsedArchiveSummary {
  version: string
  publicStrategyId: string
  publicStrategyName: string
  config: string
  configLong?: string | null
  configShort?: string | null
  symbol: string
  // The bar size the backtest ran on, as "1D".
  barSize?: string | null
  publicName?: string | null
  // Already resolved against the enum; 0 means the archive named nothing usable.
  riskProfile: number
  // Which sides the script takes, as its config states. Unknown where the config does not say.
  direction: StrategyDirection
  // Where the orders actually go, as the manifest names them - fully qualified, e.g.
  // "NASDAQ:MSTX". Absent where the export states no pair.
  bullSymbol?: string | null
  bearSymbol?: string | null
  chartFrom: string
  chartTo: string
  // The Start Limit the export stated. Null where it named none.
  backtestFrom: string | null
  exportedOn: string
  tradeCount: number
}

export interface ParsedArchiveStat {
  key: string
  label: string
  displayOrder: number
  value: string
  numericValue: number
  outOf?: number | null
  format: string
  tone: string
}

export interface ParsedArchiveTrade {
  tradeNumber: number
  direction: string
  entryDate: string
  entryPrice?: number | null
  exitDate: string
  exitPrice?: number | null
  returnPct: number
  returnValue: string
}

export interface ParsedArchiveAnnualisedReturn {
  series: string
  period: string
  label: string
  value: string
  numericValue: number
  displayOrder: number
}

// A chart the archive can draw, already drawn — so a preview shows the real thing.
export interface ParsedArchiveChart {
  kind: string
  payload: StrategyChartPayload
}

// The shared package owns this shape; a second copy here could only ever be out of date, which
// it was. An alias rather than a re-export because ParsedArchiveChart above refers to the name.
export type StrategyChartPayload = ChartPayload

export interface ParseTradingStrategyArchiveResponse {
  summary: ParsedArchiveSummary
  stats: ParsedArchiveStat[]
  annualisedReturns: ParsedArchiveAnnualisedReturn[]
  trades: ParsedArchiveTrade[]
  availableChartKinds: string[]
  charts: ParsedArchiveChart[]
}

// Request type for POST /api/trading-strategies
export interface CreateTradingStrategyRequest {
  publicStrategyId: string
  publicStrategyName: string
  publicName: string
  description: string
  monthlyPriceUsd: number
  yearlyPriceUsd: number
  showOnBrochureSite: boolean
  assetClass: number
  timeframe: number
  riskProfile: number
  // Used only where the archive states no sides of its own.
  direction: StrategyDirection
  chartFrom: string
  chartTo: string
  // The earliest date TradingView will let a customer backtest from. Null where the export
  // did not state one.
  backtestFrom: string | null
  // Where the script is published on TradingView. Null where staff recorded none.
  publicationUrl: string | null
  chartedSymbolId: string
  bullSymbolId: string | null
  bearSymbolId: string | null
  stats: TradingStrategyStatRequest[]
  charts: TradingStrategyChartRequest[]
  // Required on create: every strategy originates from a backtest.
  archive: File
  archiveVersion: StrategyArchiveVersion
}

// Response type for POST /api/trading-strategies
export interface CreateTradingStrategyResponse {
  id: string
}

// Request type for PUT /api/trading-strategies/{id}
export interface UpdateTradingStrategyRequest {
  publicStrategyId: string
  publicStrategyName: string
  publicName: string
  description: string
  monthlyPriceUsd: number
  yearlyPriceUsd: number
  showOnBrochureSite: boolean
  assetClass: number
  timeframe: number
  riskProfile: number
  // A replacement archive that states its own sides wins over this.
  direction: StrategyDirection
  chartFrom: string
  chartTo: string
  // The earliest date TradingView will let a customer backtest from. Null where the export
  // did not state one.
  backtestFrom: string | null
  // Where the script is published on TradingView. Null clears the one the strategy holds.
  publicationUrl: string | null
  chartedSymbolId: string
  bullSymbolId: string | null
  bearSymbolId: string | null
  stats: TradingStrategyStatRequest[]
  charts: TradingStrategyChartRequest[]
  // Omitted keeps the archive the strategy already has, and everything derived from it.
  archive?: File | null
  archiveVersion: StrategyArchiveVersion
}

// Response type for GET /api/trading-strategies/check-public-strategy-id-exists
export interface CheckTradingStrategyIdExistsResponse {
  exists: boolean
}

// Item in the strategies active-subscriber list.
export interface TradingStrategySubscriberItem {
  customerId: string
  customerName: string
  email: string
  // The customer's TradingView handle as recorded on the subscription, if one was. It does not follow a
  // rename.
  invitedUsername: string | null
  subscriptionStatus: number
  activeFrom: string
  activeTo: string | null
  // Cancellation lives here, not in subscriptionStatus — without it this list cannot tell a
  // winding-down subscription from a healthy one and contradicts the customer detail page.
  cancelledAt: string | null
  notes: string | null
}

// Response type for GET /api/trading-strategies/{id}/subscribers
export interface GetTradingStrategySubscribersResponse {
  subscribers: TradingStrategySubscriberItem[]
}

export const getTradingStrategyStatToneText = (tone: TradingStrategyStatTone): string => {
  switch (tone) {
    case TradingStrategyStatTone.Neutral:
      return 'Neutral'
    case TradingStrategyStatTone.Positive:
      return 'Positive'
    case TradingStrategyStatTone.Negative:
      return 'Negative'
    default:
      return 'Unknown'
  }
}

export const getTradingStrategyStatusText = (status: TradingStrategyStatus): string => {
  switch (status) {
    case TradingStrategyStatus.Draft:
      return 'Draft'
    case TradingStrategyStatus.Published:
      return 'Published'
    case TradingStrategyStatus.Archived:
      return 'Archived'
    default:
      return 'Unknown'
  }
}

export interface GenerateTradingStrategyPublicNameResponse {
  publicName: string
}

export interface GenerateTradingStrategyDescriptionResponse {
  description: string
}
